import { addDays, format, parseISO } from 'date-fns';
import { humanizeCode } from './text';

/**
 * Where somebody stands in their employment, on a given day — one rule for
 * every screen that shows it: the console's list badge, the profile header,
 * the Employment tab, the HR overview, and the mobile profile. The server's
 * counts and filters (apps/api employees/workforce.ts) apply the same rule in
 * its own terms, so a figure and the badges under it cannot disagree.
 *
 * The last working day is worked: up to and including it the person is
 * separating; from the day after, separated. A separation is about somebody
 * who has started — before the start date the person reads "Starts …", and
 * a last working day before the start date means they never will: the hire
 * was withdrawn.
 */

/** Statuses that mean the separation has already been recorded. */
export const SEPARATED_EMPLOYMENT_STATUSES: ReadonlySet<string> = new Set([
  'resigned',
  'ended',
  'terminated',
  'retired',
  // The mobile contract's one word for ended, terminated and retired.
  'separated',
]);

export type EmploymentPhase =
  /** Hired, start date still to come. */
  | 'not_started'
  /** Hired, then separated before the start date: never started. */
  | 'withdrawn'
  /** Started, no separation on record. */
  | 'employed'
  /** Started, last working day today or still to come. */
  | 'separating'
  /** Last working day passed (or a separated status with no date on record). */
  | 'separated';

export interface EmploymentStanding {
  phase: EmploymentPhase;
  /**
   * The status in a badge or header: "Regular", "Starts 15 Oct 2026",
   * "Separating — leaving on 09 Oct 2026", "Separating — last day today",
   * "Separated on 30 Sep 2026", "Did not start".
   */
  label: string;
  /**
   * The separation in a sentence, or null with none on record: "Separating —
   * leaving on 09 Oct 2026", "Separating — last day today", "Resigned —
   * separated on 30 Sep 2026", "Starts 15 Oct 2026 — leaving on 29 Oct 2026", "Did not start —
   * hire withdrawn".
   */
  separation: string | null;
}

/** `09 Oct 2026`, the product's date. */
function day(value: string): string {
  return format(parseISO(value), 'dd MMM yyyy');
}

/**
 * @param today the business calendar day, `yyyy-MM-dd` (the organisation's,
 *   not UTC's).
 */
export function employmentStanding(
  employee: {
    employmentStatus: string;
    dateHired?: string | null;
    separationDate?: string | null;
  },
  today: string,
): EmploymentStanding {
  const status = employee.employmentStatus;
  const hired = employee.dateHired?.slice(0, 10) || null;
  const last = employee.separationDate?.slice(0, 10) || null;
  const separatedStatus = SEPARATED_EMPLOYMENT_STATUSES.has(status);

  const notStarted = status === 'pre_hire' || (!!hired && hired > today);
  if (notStarted) {
    const withdrawn = (!!last && !!hired && last < hired) || (separatedStatus && !last);
    if (withdrawn) {
      return {
        phase: 'withdrawn',
        label: 'Did not start',
        separation: 'Did not start — hire withdrawn',
      };
    }
    const starts = hired && hired > today ? `Starts ${day(hired)}` : humanizeCode(status);
    return {
      phase: 'not_started',
      label: starts,
      separation: last ? `${starts} — leaving on ${day(last)}` : null,
    };
  }

  if (last && last >= today) {
    const words =
      last === today ? 'Separating — last day today' : `Separating — leaving on ${day(last)}`;
    return { phase: 'separating', label: words, separation: words };
  }
  if (last) {
    /*
     * The badge says what the tile and the filter above it say — separated,
     * and when — not the reason: "Resigned" under a "Separated" tile read as a
     * third state. The reason is on the separation line instead.
     */
    /*
     * Separated on the day after the last working day — the day the person
     * became separated. "Separated on 20 Oct" for somebody who worked 20 Oct
     * contradicted the offboarding case's "Last working day 20 Oct 2026;
     * separated from 21 Oct 2026".
     */
    const from = format(addDays(parseISO(last), 1), 'dd MMM yyyy');
    const separatedOn = `Separated on ${from}`;
    return {
      phase: 'separated',
      label: separatedOn,
      separation: separatedStatus ? `${humanizeCode(status)} — separated on ${from}` : separatedOn,
    };
  }
  return {
    phase: separatedStatus ? 'separated' : 'employed',
    label: humanizeCode(status),
    separation: null,
  };
}

/**
 * When a leaver goes, in the one sentence every offboarding screen uses — the
 * case panel, the Start and Complete confirmations and their toasts:
 * "Last working day 16 Oct 2026; separated from 17 Oct 2026". The last day is
 * worked, so the person is separated from the day after it. No full stop, so
 * it can sit inside a longer sentence.
 *
 * @param lastWorkingDay `yyyy-MM-dd` (a longer ISO string is cut to its date).
 */
export function separationTiming(lastWorkingDay: string): string {
  const last = lastWorkingDay.slice(0, 10);
  return `Last working day ${day(last)}; separated from ${format(addDays(parseISO(last), 1), 'dd MMM yyyy')}`;
}
